// @flow
import _ from 'lodash';
import uuidV4 from 'uuid/v4';

import type { MemberType, ApplicantType } from '../../types/member';
import type { IfpAppType } from '../../types/ifp-app';

type MinMaxAgeArgumentsType = {
  memberType: string,
  appType: string,
  isCovered: boolean,
};

export function formatDrugsForApplicants(drugs: Array<Object>) {
  return _.map(drugs, ({ drug_json: drug }) => (
    {
      dose_id: drug.dose.id,
      freq: {
        // time period correlates to yearly frequency, 3 for monthly, 2 for weekly etc.
        timePeriodId: 4,
        intake: drug.fillCount * drug.fillQty,
      },
      id: drug.dose.drugId,
      name: drug.product,
      ndc: drug.dose.ndc,
    }
  ));
}

// returns a new array, does not mutate the original
export default class MemberUtils {
  static uuid() {
    return uuidV4();
  }

  static mergeMemberArrays(members1: Array<MemberType>, members2: Array<MemberType>)
    : Array<MemberType> {
    return this.sortMembers(_.uniqBy(members2.concat(members1), 'id'));
  }

  static sortMembers(members: Array<MemberType>): Array<MemberType> {
    return members.slice(0).sort((a, b) => {
      if (a.type === 'primary') {
        return -1;
      }

      if (b.type === 'primary') {
        return 1;
      }

      if (a.type === 'spouse') {
        return -1;
      }

      if (b.type === 'spouse') {
        return 1;
      }

      return (b.age || -1) - (a.age || -1);
    });
  }

  // age limits are inclusive
  static getMinMaxAge({
    memberType,
    appType,
    isCovered = true,
  }: MinMaxAgeArgumentsType): { min: number, max: number } {
    const isHealth = appType === 'health';
    const isPrimary = memberType === 'primary';
    const isSpouse = memberType === 'spouse';

    let min = 0;
    let max = 100;

    if (isPrimary) {
      min = 18;
    }

    if (isHealth && isCovered) {
      if (isPrimary || isSpouse) {
        max = 64;
      } else {
        max = 25;
      }
    }

    return { min, max };
  }

  static mapMembersToApplicants(
    members: Array<MemberType>,
    { member_drugs, member_providers }: IfpAppType,
  ): Array<ApplicantType> {
    return _.map(members, ({ gender, type, age, id, tobacco, illnesses }) => (
      {
        gender,
        type,
        age,
        uuid: id,
        drugs: formatDrugsForApplicants(_.filter(member_drugs, ['member_id', id])),
        providers: type === 'primary' ? _.map(member_providers, 'provider_id') : [],
        tobacco: tobacco || false,
        illnesses: illnesses || [],
      }
    ));
  }
}
